import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ArrowDownLeft, ArrowUpRight, Banknote, CreditCard, Link2, Plus, RefreshCw } from "lucide-react";
import { AssetLogo } from "@/components/wallet/AssetLogo";
import { Gate } from "@/components/wallet/Gate";
import { XrpLogo } from "@/components/wallet/XrpLogo";
import { useWallet } from "@/lib/wallet/store";
import { overviewQuery, priceQuery } from "@/lib/wallet/queries";
import {
  addressFingerprint,
  formatAmount,
  formatLedgerTime,
  formatUsd,
  truncateAddress,
} from "@/lib/wallet/format";

export const Route = createFileRoute("/")({
  staticData: { sitemap: true },
  head: () => ({
    meta: [
      { title: "Cobalt Wallet — Passkey-secured XRPL wallet" },
      {
        name: "description",
        content:
          "Hold XRP, issued tokens and NFTs in a non-custodial XRP Ledger wallet. Keys are generated on your device and unlocked with a passkey.",
      },
      { property: "og:title", content: "Cobalt Wallet — Passkey-secured XRPL wallet" },
      {
        property: "og:description",
        content: "Non-custodial XRP Ledger wallet with passkey-encrypted local keys.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: () => (
    <Gate>
      <HomeScreen />
    </Gate>
  ),
});

function HomeScreen() {
  const { address, settings } = useWallet();
  const overview = useQuery(overviewQuery(address));
  const price = useQuery(priceQuery(settings.fiatLookup));

  const data = overview.data;
  const usd = price.data ?? null;
  const totalUsd = data && usd ? data.xrp * usd : null;
  // Until the first ledger read resolves there is no balance to state — show a
  // placeholder rather than a misleading "0".
  const loadingBalance = !data;
  const primaryValue = loadingBalance
    ? "—"
    : settings.fiatLookup && totalUsd !== null
      ? formatUsd(totalUsd)
      : formatAmount(data.xrp);
  const primaryUnit = loadingBalance
    ? ""
    : settings.fiatLookup && totalUsd !== null
      ? "USD"
      : "XRP";
  const dash = (value: string) => (loadingBalance ? "—" : value);

  return (
    <div className="space-y-4">
      <h1 className="sr-only">Cobalt Wallet dashboard</h1>
      <section className="animate-rise panel p-4">
        <div className="flex items-center gap-2">
          <span className="readout text-[11px] text-muted">
            {truncateAddress(address ?? "", 6, 6)}
          </span>
          <span className="h-3 w-px bg-border" />
          <span className="readout grid h-4 place-items-center rounded bg-accent/10 px-1.5 text-[10px] text-accent">
            {addressFingerprint(address ?? "")}
          </span>
          <button
            type="button"
            aria-label="Refresh"
            onClick={() => void overview.refetch()}
            className="ml-auto grid size-6 place-items-center rounded-md border border-border text-faint transition-colors hover:text-accent"
          >
            <RefreshCw className={`size-3 ${overview.isFetching ? "animate-spin" : ""}`} />
          </button>
        </div>

        <p className="label-instrument mt-4">
          {settings.fiatLookup ? "Total balance" : "XRP balance"}
        </p>
        <div className="flex items-end gap-2">
          <span className="mb-0.5 grid size-10 shrink-0 place-items-center rounded-full bg-background p-2">
            <XrpLogo className="size-full" />
          </span>
          <p
            className={`readout text-[38px] font-medium leading-none tracking-tight ${
              loadingBalance ? "text-faint" : ""
            }`}
          >
            {primaryValue}
          </p>
          <p className="readout mb-1 text-[13px] text-muted">{primaryUnit}</p>
        </div>

        <div className="mt-4 flex items-center justify-between border-t border-border pt-3">
          <div className="flex items-center gap-2.5">
            <span className="grid size-8 shrink-0 place-items-center rounded-full bg-background p-1.5">
              <XrpLogo className="size-full" />
            </span>
            <div>
              <p className="label-instrument">XRP</p>
              <p className="readout text-[15px]">{dash(formatAmount(data?.xrp ?? 0, true))}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="label-instrument">Free to send</p>
            <p className="readout text-[15px] text-accent">
              {dash(formatAmount(data?.spendable ?? 0, true))}
            </p>
          </div>
        </div>

        <div className="mt-3">
          <div className="readout flex items-center justify-between text-[10px]">
            <span className="text-faint">RESERVE</span>
            <span className="text-muted">
              {formatAmount(data?.reserve ?? 0, true)} XRP locked · {data?.ownerCount ?? 0} objects
            </span>
          </div>
          <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-elevated">
            <div
              className="h-full rounded-full bg-accent"
              style={{
                width: `${Math.min(100, data && data.xrp > 0 ? (data.reserve / data.xrp) * 100 : 0)}%`,
              }}
            />
          </div>
        </div>

        {data && !data.funded ? (
          <Link
            to="/fund"
            className="readout mt-3 flex items-center justify-between gap-2 rounded-lg border border-accent/40 bg-accent/10 p-2.5 text-[10px] leading-relaxed text-accent"
          >
            <span>
              Account not yet activated on mainnet. Send at least {formatAmount(data.baseReserve)}{" "}
              XRP to this address to fund it.
            </span>
            <span className="shrink-0 uppercase tracking-[0.12em]">Fund →</span>
          </Link>
        ) : null}


        {overview.isError ? (
          <p className="readout mt-3 rounded-lg border border-danger/40 bg-danger/10 p-2.5 text-[10px] text-danger">
            Ledger unreachable. Balances shown may be stale.
          </p>
        ) : null}
      </section>

      <div className="animate-rise grid grid-cols-4 gap-2.5 [animation-delay:60ms]">
        <Link
          to="/buy"
          className="panel flex flex-col items-center justify-center gap-1.5 py-3 transition-colors hover:bg-surface2"
        >
          <CreditCard className="size-4 text-accent" />
          <span className="readout text-[10px] uppercase tracking-[0.12em]">Buy</span>
        </Link>
        <Link
          to="/sell"
          className="panel flex flex-col items-center justify-center gap-1.5 py-3 transition-colors hover:bg-surface2"
        >
          <Banknote className="size-4 text-accent" />
          <span className="readout text-[10px] uppercase tracking-[0.12em]">Sell</span>
        </Link>
        <Link
          to="/send"
          className="panel flex flex-col items-center justify-center gap-1.5 py-3 transition-colors hover:bg-surface2"
        >
          <ArrowUpRight className="size-4 text-accent" />
          <span className="readout text-[10px] uppercase tracking-[0.12em]">Send</span>
        </Link>
        <Link
          to="/receive"
          className="panel flex flex-col items-center justify-center gap-1.5 py-3 transition-colors hover:bg-surface2"
        >
          <ArrowDownLeft className="size-4 text-accent" />
          <span className="readout text-[10px] uppercase tracking-[0.12em]">Receive</span>
        </Link>
      </div>


      <section className="animate-rise [animation-delay:120ms]">
        <div className="mb-2.5 flex items-center justify-between">
          <p className="readout text-[11px] uppercase tracking-[0.15em] text-muted">Trustlines</p>
          <Link to="/trustlines" search={{ q: "" }} className="readout flex items-center gap-1 text-[10px] text-accent">
            <Plus className="size-3" /> Add
          </Link>
        </div>
        <div className="panel divide-y divide-border">
          {(data?.tokens ?? []).length === 0 ? (
            <p className="readout p-4 text-[11px] text-faint">
              No issued tokens held. Add a trustline to hold stablecoins or issued assets.
            </p>
          ) : (
            data!.tokens.map((token) => (
              <div key={`${token.currency}-${token.issuer}`} className="flex items-center gap-3 px-3.5 py-3">
                <AssetLogo asset={token} />
                <div className="min-w-0">
                  <p className="text-[13px] font-medium">
                    {token.code}
                    {token.frozen ? (
                      <span className="readout ml-1.5 text-[9px] text-danger">FROZEN</span>
                    ) : null}
                  </p>
                  <p className="readout truncate text-[10px] text-faint">
                    {truncateAddress(token.issuer, 4, 4)}
                  </p>
                </div>
                <div className="ml-auto text-right">
                  <p className="readout text-[13px]">{formatAmount(token.balance, true)}</p>
                  <p className="readout text-[10px] text-faint">limit {formatAmount(token.limit)}</p>
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      <section className="animate-rise [animation-delay:180ms]">
        <div className="mb-2.5 flex items-center justify-between">
          <p className="readout text-[11px] uppercase tracking-[0.15em] text-muted">Recent</p>
          <Link to="/activity" className="readout text-[10px] text-accent">See all</Link>
        </div>
        <div className="panel divide-y divide-border">
          {(data?.activity ?? []).length === 0 ? (
            <p className="readout p-4 text-[11px] text-faint">No ledger activity yet.</p>
          ) : (
            data!.activity.slice(0, 4).map((item) => (
              <div key={item.hash} className="flex items-center gap-3 px-3.5 py-3">
                <div
                  className={`grid size-8 place-items-center rounded-full text-[13px] ${
                    !item.succeeded
                      ? "bg-danger/10 text-danger"
                      : item.kind === "TrustSet"
                        ? "border border-accent/40 bg-accent/12 text-accent"
                        : item.direction === "in"
                          ? "bg-success/10 text-success"
                          : "bg-elevated text-muted"
                  }`}
                >
                  {item.kind === "TrustSet" ? (
                    <Link2 className="size-4" />
                  ) : item.direction === "in" ? (
                    <ArrowDownLeft className="size-4" />
                  ) : (
                    <ArrowUpRight className="size-4" />
                  )}
                </div>
                <div className="min-w-0">
                  <p className="text-[13px] font-medium">
                    {item.kind === "Payment"
                      ? `${item.direction === "in" ? "Received" : "Sent"} ${item.code || "XRP"}`
                      : item.kind === "TrustSet"
                        ? `Trustline set${item.code ? ` · ${item.code}` : ""}`
                        : item.kind}
                    {!item.succeeded ? (
                      <span className="readout ml-1.5 text-[9px] text-danger">FAILED</span>
                    ) : null}
                  </p>

                  <p className="readout text-[10px] text-faint">
                    {truncateAddress(item.counterparty, 4, 4)} · {formatLedgerTime(item.date)}
                  </p>
                </div>
                <p
                  className={`readout ml-auto text-[13px] ${
                    item.direction === "in" ? "text-success" : "text-muted"
                  }`}
                >
                  {item.direction === "in" ? "+" : "−"}
                  {formatAmount(item.amount, true)}
                </p>
              </div>
            ))
          )}
        </div>
      </section>
    </div>
  );
}
